if (!customElements.get("variant-picker")) {
  const REMEMBERED_SIZE_KEY = 'bbs.remembered_size.v1';

  const RememberedSize = {
    read() {
      try {
        return localStorage.getItem(REMEMBERED_SIZE_KEY) || null;
      } catch (e) {
        return null;
      }
    },
    write(sizeValue) {
      if (!sizeValue) return;
      try {
        localStorage.setItem(REMEMBERED_SIZE_KEY, sizeValue);
      } catch (e) {
        // Quota, disabled, or private-mode — silently skip
      }
    },
  };

  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this._baseAbortController = null;
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll('.m-product-option select, .m-product-option input');
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });
      return result;
    }

    connectedCallback() {
      this._baseAbortController?.abort();
      this._baseAbortController = new AbortController();

      this.addEventListener('change', (event) => {
        const target = this.getInputForEventTarget(event.target);
        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, { data: { selectedOptionValues: this.selectedOptionValues, event, target } });
      }, { signal: this._baseAbortController.signal });
    }

    disconnectedCallback() {
      this._baseAbortController?.abort();
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }
  }

  class VariantPickerLuxury extends VariantPicker {
    static _stateCache = new Map();

    constructor() {
      super();
      this.steps = [];
      this.currentStep = 1;
      this.selections = {};
      this.totalSteps = 0;

      this.isPreviewMode = false;
      this.originalVariantId = null;
      this._allVariants = null;
      this._stepAbortController = null;
      this._isInitialized = false;
    }

    connectedCallback() {
      super.connectedCallback();

      this.mode = this.dataset.mode;

      if (this.mode === 'sequential') {
        this.initSequentialMode();
        this.loadAllVariants();
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this._stepAbortController?.abort();
      this.saveState();
    }

    // --- State persistence across DOM re-renders ---

    saveState() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      const state = {
        selections: { ...this.selections},
        currentStep: this.currentStep,
        timestamp: Date.now()
      };

      try {
        sessionStorage.setItem(`variant-picker-${sectionId}`, JSON.stringify(state));
      } catch (e) {
        console.warn('Could not save variant picker state', e);
      }
    }

    restoreState() {
      const sectionId = this.dataset.sectionId;
      const cached = sessionStorage.getItem(`variant-picker-${sectionId}`);
      if (!cached) return false;

      try {
        const state = JSON.parse(cached);
        // restore if cache if fresh
        const MAX_AGE = 3000;
        const age = Date.now() - (state.timestamp || 0);

        if (age > MAX_AGE) {
          sessionStorage.removeItem(`variant-picker-${sectionId}`);
          return false;
        }

        const currentOptionNames = this.steps.map((s) => s.dataset.optionName);
        const cachedOptionNames = Object.keys(state.selections);
        const isValid = cachedOptionNames.every((name) => currentOptionNames.includes(name));

        if (!isValid) {
          sessionStorage.removeItem(`variant-picker-${sectionId}`);
          return false;
        }

        this.selections = state.selections;
        this.currentStep = state.currentStep;
        return true;
      } catch (e) {
        sessionStorage.removeItem(`variant-picker-${sectionId}`);
        return false;
      }
    }

    // --- Sequential mode initialization ---

    initSequentialMode() {
      this.steps = Array.from(this.querySelectorAll('.variant-option-step'));
      this.totalSteps = this.steps.length;

      if (this.totalSteps === 0) {
        console.warn('VariantPickerLuxury: No variant steps found.');
        return;
      }

      // Check if we have cached state ( indicates re-render scenario )
      const sectionId = this.dataset.sectionId;
      const hasCachedState = sessionStorage.getItem(`variant-picker-${sectionId}`);

      if (hasCachedState && this.restoreState()) {
        // Re-rendor scenario: restore state and visibility
        this.restoreStepVisibility();
        this.restoreInputValues();
      } else {
        // Fresh load: initialize steps
        this.enableStep(1);
        for (let i = 2; i <= this.totalSteps; i++) {
          this.disableStep(i);
        }
        // Clear Shopify's pre-selections on disabled steps
        for (let i = 2; i <= this.totalSteps; i++) {
          const step = this.steps[i -1];
          step.querySelectorAll('input[type="radio"]').forEach(radio => {
            radio.checked = false;
          });
          step.querySelectorAll('select').forEach(select => {
            select.selectedIndex = 0;
          });
        }
        this.detectPreselectedOptions();
      }

      this.setupSequentialHandlers();

      // Check and update buy buttons based on current selections
      this.checkAndUpdateBuyButton();

    }

    detectPreselectedOptions() {
      for (let i = 1; i <= this.totalSteps; i++) {
        const step = this.steps[i - 1];
        const optionName = step.dataset.optionName;

        // Skip if already have a selection from restored state
        if (this.selections[optionName] !== undefined) continue;

        const radios = Array.from(step.querySelectorAll('input[type="radio"]:not(:disabled)'));
        const select = step.querySelector('select');

        let selectedValue = null;

        // ONLY auto-select if exactly ONE option - ignore Shopify pre-selections
        if (radios.length  === 1) {
          radios[0].checked = true;
          selectedValue = radios[0].value;
        } else if (radios.length > 1) {
          radios.forEach(r => r.checked = false);
          break;
        }

        if (!selectedValue && select) {
          const availableOptions = Array.from(select.options).filter(opt => !opt.disabled && opt.value);

          // Check if select already has a selected value (Shopify pre-selection)
          if (availableOptions.length === 1) {
            select.value = availableOptions[0].value;
            selectedValue = availableOptions[0].value;
          } else if (availableOptions.length > 1) {
            select.selectedIndex = 0;
            break;
          }
        }

        if (selectedValue) {
          this.selections[optionName] = selectedValue;
          this.updateSelectedDisplay(step, selectedValue);
          step.classList.add('has-selection');

          if (i < this.totalSteps) {
            this.enableStep(i + 1);
            this.currentStep = i + 1;
          }
        } else {
          break;
        }
      }
    }

    isSizeStep(step) {
      if (!step) return false;
      const optionName = (step.dataset.optionName || '').toLowerCase();
      let sizeNames = [];
      try {
        sizeNames = JSON.parse(this.dataset.sizeOptionNames || '[]').map(s => s.toLowerCase());
      } catch (e) {
        sizeNames = ['size'];
      }
      return sizeNames.includes(optionName);
    }

    restoreRememberedSize(stepNumber) {
      const step = this.steps[stepNumber - 1];
      if (!step || !this.isSizeStep(step)) return;

      // Guard: already have a selection (from restored sessionStorage or previous restore)
      const optionName = step.dataset.optionName;
      if (this.selections[optionName] !== undefined) return;

      const remembered = RememberedSize.read();
      if (!remembered) return;

      // URL variant param wins - bail if user deep-linked to a specific variant
      //if (new URL(window.location.href).searchParams.get('variant')) return;

      // Try radio first, then select. Only pick :not(:disabled) - the step is
      // now enabled (we're inside enableStep's double-rAF), so unavailable
      // size stay :disabled per updateOptionAvailability and skipped.
      const radios = step.querySelectorAll('input[type="radio"]:not(:disabled)');
      for (const radio of radios) {
        if (radio.value === remembered) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', {bubbles: true}));
          return;
        }
      }

      const select = step.querySelector('select');
      if (select) {
        const match = Array.from(select.options).find(o => o.value === remembered && !o.disabled);
        if (match) {
          select.value = remembered;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      // No match -> remembered size unavailable for this product -> silenly no-op
    }

    restoreStepVisibility() {
      for (let i = 1; i <= this.totalSteps; i++) {
        const step = this.steps[i - 1];
        const optionName = step.dataset.optionName;

        if (this.selections[optionName] !== undefined) {
          this.enableStep(i);
          this.updateSelectedDisplay(step, this.selections[optionName]);
          step.classList.add('has-selection');
        } else if (i <= this.currentStep) {
          this.enableStep(i);
        } else {
          this.disableStep(i);
        }
      }
    }

    restoreInputValues() {
      for (const [optionName, value] of Object.entries(this.selections)) {
        const step = this.steps.find(s => s.dataset.optionName === optionName);
        if (!step) continue;

        // Restore radio selection
        const radios = step.querySelectorAll('input[type="radio"]');
        radios.forEach(radio => {
          radio.checked = (radio.value === value);
        });

        // Restore select selection
        const select = step.querySelector('select');
        if (select) {
          select.value = value;
        }
      }
    }

    setupSequentialHandlers() {
      this._stepAbortController?.abort();
      this._stepAbortController = new AbortController();
      const signal = this._stepAbortController.signal;

      this.steps.forEach((step) => {
        const stepNumber = parseInt(step.dataset.step, 10);

        step.addEventListener('change', (event) => {
          const target = event.target;

          // Handle radio button changes
          if (target.type === 'radio' && target.checked) {
            this.handleStepSelection(stepNumber, target);
          }

          // Handle select changes
          if (target.tagName === 'SELECT') {
            const selectedOption = target.options[target.selectedIndex];
            if (selectedOption && selectedOption.value) {
              this.handleStepSelection(stepNumber, selectedOption);
            }
          }
        }, {signal});

        // Add click handler on labels as backup for custom-styled inputs
        step.querySelectorAll('.m-product-option--node__label').forEach((label) => {
          label.addEventListener('click', () => {
            // Small delay to ensure input state updates
            requestAnimationFrame(() => {
              const inputId = label.getAttribute('for');
              const input = inputId ? document.getElementById(inputId) : label.previousElementSibling;

              if (input && input.type === 'radio' && input.checked && !input.disabled) {
                // Only trigger if not already handled by change event
                if (!this.selections[step.dataset.optionName] || this.selections[step.dataset.optionName] !== input.value) {
                  this.handleStepSelection(stepNumber, input);
                }
              }
            })
          }, { signal});
        });
      });
    }

    // --- Step state management ---

    enableStep(stepNumber) {
      const step = this.steps[stepNumber - 1];
      if (!step) return;

      step.dataset.enable = 'true';
      step.classList.remove('is-disabled');
      step.classList.add('is-enabling');

      const inputs = step.querySelectorAll('input, select');
      inputs.forEach((input) => {
        input.disabled = false;
        input.setAttribute('tabindex', '0');
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          step.classList.remove('is-enabling');
          step.classList.add('is-enabled');
           
          // after step is enabled, try to auto-select remembered size
          this.restoreRememberedSize(stepNumber);
        });
      });
    }

    disableStep(stepNumber) {
      const step = this.steps[stepNumber - 1];
      if (!step) return;

      step.dataset.enable = 'false';
      step.classList.remove('is-enabled', 'is-enabling', 'has-selection');
      step.classList.add('is-disabled');

      const inputs = step.querySelectorAll('input, select');
      inputs.forEach((input) => {
        input.disabled = true;
        input.setAttribute('tabindex', '-1');
      });
    }

    // --- Selection handling ---

    handleStepSelection(stepNumber, input) {
      const step = this.steps[stepNumber - 1];
      if (!step) return;

      // Safty check: ensure input has a valid value
      if (!input || !input.value) {
        console.warn('handleStepSelection: Invalid input', { stepNumber, input });
        return;
      }

      const optionName = step.dataset.optionName;
      const selectedValue = input.value;

      // Reset later steps if user changed an earlier selection
      if (stepNumber < this.currentStep) {
        this.resetLaterSteps(stepNumber);
      }

      // Save selection
      this.selections[optionName] = selectedValue;

      if (this.isSizeStep(step)) {
        RememberedSize.write(selectedValue);
      }

      // Update UI
      this.updateSelectedDisplay(step, selectedValue);
      step.classList.add('has-selection');

      // Handle image preview for first option (color)
      if (stepNumber === 1) {
        this.handleImagePreview(input);
      }

      // Enable next step or mark as complete
      if (stepNumber < this.totalSteps) {
        this.enableStep(stepNumber + 1);
        this.currentStep = stepNumber + 1;

        // Update availability for the next step based on current selections
        this.updateOptionAvailability(stepNumber + 1);

        this.restoreRememberedSize(stepNumber + 1);
      } else {
        // Last step - set currentStep to final step number
        this.currentStep = stepNumber;
      }

      // Persist state AFTER currentStep is updated to survive section re-renders
      this.saveState();

      // Check if all steps complete and update buy buttons
      this.checkVariantComplete();
    }

    resetLaterSteps(fromStep) {
      for (let i = fromStep + 1; i <= this.totalSteps; i++) {
        const step = this.steps[i - 1];
        if (!step) continue;

        const optionName = step.dataset.optionName;
        delete this.selections[optionName];

        step.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.checked = false;
        });

        step.querySelectorAll('select').forEach((select) => {
          select.selectedIndex = 0;
        });

        this.updateSelectedDisplay(step, '');
        step.classList.remove('has-selection');
        this.disableStep(i);
      }

      // Disable buy buttons when steps are reset
      this.disableBuyButtons();
    }

    updateSelectedDisplay(step, value) {
      const display = step.querySelector('[data-selected-value]');
      if (display) {
        display.textContent = value;
      }
    }

    checkVariantComplete() {
      const allSelected = this.steps.every((step) => {
        const optionName = step.dataset.optionName;
        return this.selections[optionName] !== undefined;
      });

      if (allSelected) {
        // Clear session storage since selection is complete
        const sectionId = this.dataset.sectionId;
        if (sectionId) {
          sessionStorage.removeItem(`variant-picer-${sectionId}`);
        }

        this.isPreviewMode = false;
        this.originalVariantId = null;

        // Enable buy buttons when all options selectd
        this.enableBuyButtons();

        this.dispatchEvent(new CustomEvent('variant:complete', {
          detail: { selections: this.selections },
          bubbles: true
        }));
      } else {
        // Disable buy buttons if selection incomplete
        this.disableBuyButtons();
      }
    }

    disableBuyButtons() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      // Select all buy-related buttons
      const buyButtons = productInfo.querySelectorAll(
        '[name="add"], .m-add-to-cart, .shopify-payment-button button, .m-product-quick-checkout, [data-add-to-cart]'
      );

      buyButtons.forEach((btn) => {
        btn.disabled = true;
        btn.classList.add('is-disabled', 'variant-incomplete');
        btn.setAttribute('aria-disabled', 'true');
      });

      // Dispatch event for other components to listen
      this.dispatchEvent(new CustomEvent('variant:incomplete', {
        detail: {
          selections: this.selections,
          totalSteps: this.totalSteps,
          completedSteps: Object.keys(this.selections).length
        },
        bubbles: true
      }));
    }

    enableBuyButtons() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      // Select all buy-related buttons
      const buyButtons = productInfo.querySelectorAll(
        '[name="add"], .m-add-to-cart, .shopify-payment-button button, .m-product-quick-checkout, [data-add-to-cart]'
      );

      buyButtons.forEach((btn) => {
        btn.disabled = false;
        btn.classList.remove('is-disabled', 'variant-incomplete');
        btn.setAttribute('aria-disabled', 'false');
      });
    }

    checkAndUpdateBuyButton() {
      const allSelected = this.steps.every((step) => {
        const optionName = step.dataset.optionName;
        return this.selections[optionName] !== undefined
      });

      if (allSelected) {
        this.enableBuyButtons();
      } else {
        this.disableBuyButtons();
      }
    }

    // --- Dynamic availability based on selections ---

    updateOptionAvailability(forStepNumber) {
      // Wait for variants to load
      if (!this._allVariants || this._allVariants.length === 0) {
        setTimeout(() => this.updateOptionAvailability(forStepNumber), 500);
        return;
      }

      const step = this.steps[forStepNumber - 1];
      if (!step) return;

      // Build filter based on previous selections
      // Shopify variants have option1, option2, option3 properties
      let filteredVariants = this._allVariants;

      for (let i = 1; i < forStepNumber; i++) {
        const prevStep = this.steps[i - 1];
        const prevOptionName = prevStep.dataset.optionName;
        const prevValue = this.selections[prevOptionName];

        if (prevValue) {
          const optionKey = `option${i}`;
          filteredVariants = filteredVariants.filter(v => v[optionKey] === prevValue);
        }
      }

      // Get available values for this step
      const optionKey = `option${forStepNumber}`;
      const availableValues = new Set();

      filteredVariants.forEach(v => {
        if (v.available && v[optionKey]) {
          availableValues.add(v[optionKey]);
        }
      });

      // Update UI - mark unavailable options
      const radios = step.querySelectorAll('input[type="radio"]');
      radios.forEach(radio => {
        const node = radio.closest('.m-product-option--node');
        if (!node) return;

        if (availableValues.has(radio.value)) {
          // Available
          radio.disabled = false;
          node.classList.remove('m-product-option--node__unavailable');
        } else {
          // Unavailable for this combination
          radio.disabled = true;
          node.classList.add('m-product-option--node__unavailable');
        }
      });

      // Update select options
      const select = step.querySelector('select');
      if (select) {
        Array.from(select.options).forEach(option => {
          if (!option.value) return; // Skip placeholder

          if (availableValues.has(option.value)) {
            option.disabled = false;
            option.classList.remove('unavailable');
          } else {
            option.disabled = true;
            option.classList.add('unavailable');
          }
        });
      }
    }

    // --- Variant data & image preview ---

    loadAllVariants() {
      if (this._allVariants) return;

      const productUrl = this.dataset.productUrl;

      // Validate URL exists and is a valid product URL
      if (!productUrl || !productUrl.startsWith('/products/')) {
        console.warn('VariantPickerLuxury: Invalid product URL:', productUrl);
        return;
      }

      fetch(`${productUrl}.js`)
        .then((res) => {
          // Check if response is OK and is JSON
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          }
          // Shopify .js endpoint returns text/javascript, not application/json
          const contentType = res.headers.get('content-type');
          if (!contentType || (!contentType.includes('application/json') && !contentType.includes('text/javascript'))) {
            throw new Error(`Expected JSON, got ${contentType}`);
          }
          return res.json();
        })
        .then((product) => {
          if (!product || !product.variants) {
            throw new Error('Invalid product data structure');
          }
          this._allVariants = product.variants.map((v) => {
            if (v.featured_image && !v.featured_media) {
              v.featured_media = {
                id: v.featured_image.id,
                position: v.featured_image.position,
              };
            }
            return v;
          });
        })
        .catch((err) => {
          console.warn('VariantPickerLuxury: Could not load variant data.', err);
        });
    }

    handleImagePreview(input) {
      if (!this._allVariants) return;

      const selectedValue = input.value;
      const step = input.closest('.variant-option-step');

      if (!step) return;

      const stepNumber = parseInt(step.dataset.step, 10);

      // Dynamic determine which option field to compare based on step number
      // Shopify variant have option1, option2, option3 properties
      const optionKey = `option${stepNumber}`;

      // Find variant where this specific option matches
      const matchingVariant = this._allVariants.find((v) => {
        return v[optionKey] === selectedValue;
      });

      if (matchingVariant && matchingVariant.featured_media) {
        this.previewVariantImages(matchingVariant);
      } else {

        const fallbackVariant = this._allVariants.find((v) => {
          return v.option1 === selectedValue ||
                 v.option2 === selectedValue ||
                 v.option3 === selectedValue;
        });

        if (fallbackVariant && fallbackVariant.featured_media) {
          this.previewVariantImages(fallbackVariant);
        }
      }
    }

    previewVariantImages(variant) {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      const gallery = productInfo.productMedia;
      if (!gallery) return;

      this.isPreviewMode = true;

      if (!this.originalVariantId) {
        const currentVariant = productInfo.currentVariant;
        this.originalVariantId = currentVariant ? currentVariant.id : null;
      }

      if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(variant);
      }

      this.dispatchEvent(new CustomEvent('variant:preview', {
        detail: { variant, isPreview: true },
        bubbles: true
      }));
    }
  }

  customElements.define("variant-picker", VariantPickerLuxury);
}
